import styled from "styled-components";
import type { ReactNode } from "react";

import { DefaultTheme } from "../themes";
import { hexToRGB } from "../../utils/style";

/**
 * [共通パーツ]ボタン
 */

type ButtonProps = {
  className?: string;
  disabled?: boolean;
  ghost?: boolean;
  buttonType?: "primary" | "danger" | "default";
  children: ReactNode;
  onClick: () => void;
};

const getColor = (props: ButtonProps & { theme: DefaultTheme }) => {
  switch (props.buttonType) {
    case "primary":
      return props.theme.colors.brand;

    case "danger":
      return props.theme.colors.caution;

    default:
      return props.theme.colors.primaryText;
  }
};

const getBackgroundColor = (props: ButtonProps & { theme: DefaultTheme }) => {
  if (props.ghost) {
    return props.theme.colors.primaryBackground;
  } else if (props.disabled) {
    return props.theme.colors.primaryText;
  } else {
    return getColor(props);
  }
};

const Inner = styled.div<{ disabled?: boolean }>`
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
`;

const transientProps = ["ghost", "buttonType"];

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !transientProps.includes(prop),
})<ButtonProps>`
  width: 100%;
  height: 100%;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  outline: 0;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  border-radius: 6px;
  border: ${(props) => (props.ghost ? `2px solid ${getColor(props)}` : "none")};
  background-color: ${(props) => getBackgroundColor(props)};
  color: ${(props) =>
    props.ghost ? getColor(props) : props.theme.colors.secondaryText};
  transition: background
    ${(props) =>
      `${props.theme.interactions.primaryEasing} ${props.theme.interactions.primaryDuration}`};
  &:hover {
    background: ${(props) => hexToRGB(getColor(props), 0.8)};
  }
`;

const StyledRoundButton = styled(StyledButton)<ButtonProps>`
  border-radius: 22px;
`;

export function Button({
  disabled = false,
  ghost = false,
  buttonType = "default",
  children,
  onClick,
  className,
}: ButtonProps) {
  return (
    <StyledButton
      className={className}
      disabled={disabled}
      ghost={ghost}
      buttonType={buttonType}
      onClick={onClick}
    >
      <Inner disabled={disabled}>{children}</Inner>
    </StyledButton>
  );
}

export function RoundButton({
  disabled = false,
  ghost = false,
  buttonType = "default",
  children,
  onClick,
}: ButtonProps) {
  return (
    <StyledRoundButton
      disabled={disabled}
      ghost={ghost}
      buttonType={buttonType}
      onClick={onClick}
    >
      <Inner disabled={disabled}>{children}</Inner>
    </StyledRoundButton>
  );
}
